import { logger } from '@tunasong/models'
import type { Entity, Persisted } from '@tunasong/schemas'
import { create } from 'zustand'
import {
  evictionGraceMs,
  forget,
  forgetAll,
  maxCachedEntries,
  releaseRef,
  retainRef,
  selectEvictable,
  touch,
} from './cache-eviction.js'

export { evictionGraceMs, maxCachedEntries } from './cache-eviction.js'

/** Key of the child index: the entity's parentId, or `null` for root entities */
export type ParentKey = string | null

export type ChildIndex = Map<ParentKey, ReadonlySet<string>>

export const toParentKey = (entity: { parentId?: string | null }): ParentKey => entity.parentId ?? null

/** One local optimistic change, numbered so the save that carried it can confirm exactly it. GH #1966 */
export interface UnconfirmedPatch {
  seq: number
  patch: Partial<Persisted<Entity>>
}

export interface GraphCacheState {
  entityCache: Map<string, Persisted<Entity>>
  /** parentId -> ids of the cached children. Sets are copy-on-write, so an untouched parent keeps its Set. */
  childIndex: ChildIndex
  /**
   * Ids of entries that carry local optimistic changes the server has not confirmed yet. A pending entry keeps
   * the server-issued `updatedAt` of the version it was edited from, so any strictly newer server or peer
   * version supersedes it, while a copy of the same version does not revert it. GH #1913
   */
  pendingIds: ReadonlySet<string>
  /**
   * Local patches per entry that no resolved save has confirmed yet, oldest first. A newer server version does
   * not replace them: it is the base they are re-applied on. The response to our own save is strictly newer than
   * the pending entry but carries only what that save sent, so replacing the entry wholesale reverted everything
   * typed while the save was in flight. GH #1966
   */
  unconfirmed: ReadonlyMap<string, readonly UnconfirmedPatch[]>
  clearCache: () => void
  getEntry: (id: string) => Persisted<Entity> | undefined
  removeEntry: (id: string) => Map<string, Persisted<Entity>>
  upsertCache: (entity: Persisted<Entity>) => Persisted<Entity>
  upsertCacheEntries: (update: Persisted<Entity>[]) => Persisted<Entity>[]
  partialUpdateCache: (id: string, partialEntity: Partial<Persisted<Entity>>) => Persisted<Entity> | undefined
  /** Apply a local, unconfirmed change. Does not create missing entries and never touches `updatedAt`. */
  optimisticUpdateCache: (id: string, partialEntity: Partial<Persisted<Entity>>) => Persisted<Entity> | undefined
  /** Sequence number of the newest unconfirmed patch on an entry, if any. */
  lastOptimisticSeq: (id: string) => number | undefined
  /** Drop the patches a resolved (or failed) save carried. Later patches stay applied. */
  confirmOptimistic: (id: string, seqs: readonly number[]) => void
  /**
   * Reference an entry while it is on screen, so eviction keeps it and the cached children of a parent with this
   * id. Counted: pair every call with `release`. Does not notify subscribers. GH #1643
   */
  retain: (id: string) => void
  /** Drop a reference taken with `retain`. The grace period of the entry and of its cached children starts now. */
  release: (id: string) => void
  /**
   * Evict cold entries, least recently touched first, until at most `maxEntries` remain. Cold means unreferenced,
   * outside its grace period and without local changes. One store write. Returns how many were evicted.
   */
  evictCold: (maxEntries?: number) => number
}

/** A copy-on-write editor for the child index; each touched parent Set is copied at most once */
const createIndexWriter = (source: ChildIndex) => {
  const index: ChildIndex = new Map(source)
  const copied = new Set<ParentKey>()
  const editable = (key: ParentKey) => {
    if (copied.has(key)) {
      return index.get(key) as Set<string>
    }
    copied.add(key)
    const siblings = new Set(index.get(key))
    index.set(key, siblings)
    return siblings
  }
  const remove = (key: ParentKey, id: string) => {
    if (!index.get(key)?.has(id)) {
      return
    }
    const siblings = editable(key)
    siblings.delete(id)
    if (siblings.size === 0) {
      index.delete(key)
      copied.delete(key)
    }
  }
  /** Move `id` from the parent of `previous` to the parent of `next`. Either side may be absent. */
  const reindex = (id: string, previous?: Persisted<Entity>, next?: Persisted<Entity>) => {
    if (previous && next && toParentKey(previous) === toParentKey(next)) {
      return
    }
    if (previous) {
      remove(toParentKey(previous), id)
    }
    if (next) {
      editable(toParentKey(next)).add(id)
    }
  }
  return { index, reindex }
}

const withoutId = (ids: ReadonlySet<string>, id: string) => {
  if (!ids.has(id)) {
    return ids
  }
  const updated = new Set(ids)
  updated.delete(id)
  return updated
}

let nextSeq = 0

const withoutKey = <V>(map: ReadonlyMap<string, V>, id: string) => {
  if (!map.has(id)) {
    return map
  }
  const updated = new Map(map)
  updated.delete(id)
  return updated
}

/**
 * A server version to store, with the entry's unconfirmed local patches re-applied on top. The server
 * `updatedAt` is kept, so ordering stays on the server clock. GH #1966
 */
const rebase = (update: Persisted<Entity>, patches?: readonly UnconfirmedPatch[]) =>
  patches?.length
    ? patches.reduce<Persisted<Entity>>(
        (entity, { patch }) => ({ ...entity, ...patch, updatedAt: update.updatedAt }),
        update
      )
    : update

/** Last-writer-wins on the server-issued `updatedAt` */
const isNewer = (update: Persisted<Entity>, cacheEntry?: Persisted<Entity>) =>
  !cacheEntry || cacheEntry.updatedAt < update.updatedAt

/** Remove the cold entries in one copy-on-write store write. GH #1643 */
const evict = (maxEntries: number) => {
  const { entityCache, childIndex, pendingIds, unconfirmed } = useGraphCacheStore.getState()
  const { ids, retryAt } = selectEvictable({ entityCache, pendingIds, unconfirmed }, maxEntries)
  if (!ids.length) {
    return { count: 0, retryAt }
  }
  // Copy before deleting: the live Map is the previous state that subscribers compare against. GH #1913
  const updatedCache = new Map(entityCache)
  const writer = createIndexWriter(childIndex)
  for (const id of ids) {
    writer.reindex(id, entityCache.get(id))
    updatedCache.delete(id)
    forget(id)
  }
  useGraphCacheStore.setState({ entityCache: updatedCache, childIndex: writer.index })
  return { count: ids.length, retryAt }
}

/** The pending idle eviction, if any. One at a time: when it fires it schedules the next one if needed. */
let evictionTimer: ReturnType<typeof setTimeout> | undefined

const cancelEvictionTimer = () => {
  clearTimeout(evictionTimer)
  evictionTimer = undefined
}

const scheduleEviction = (at: number) => {
  if (evictionTimer !== undefined) {
    return
  }
  evictionTimer = setTimeout(
    () => {
      evictionTimer = undefined
      evictOverCap()
    },
    Math.max(0, at - Date.now())
  )
}

/** Bring the cache back to its cap, and come back for entries still inside their grace period */
const evictOverCap = () => {
  if (useGraphCacheStore.getState().entityCache.size <= maxCachedEntries) {
    return
  }
  const { retryAt } = evict(maxCachedEntries)
  if (retryAt !== undefined && useGraphCacheStore.getState().entityCache.size > maxCachedEntries) {
    scheduleEviction(retryAt)
  }
}

/** Global graph cache store */
export const useGraphCacheStore = create<GraphCacheState>((set, get) => ({
  clearCache: () => {
    cancelEvictionTimer()
    forgetAll()
    set({ entityCache: new Map(), childIndex: new Map(), pendingIds: new Set(), unconfirmed: new Map() })
  },
  entityCache: new Map<string, Persisted<Entity>>(),
  childIndex: new Map(),
  pendingIds: new Set<string>(),
  unconfirmed: new Map(),
  getEntry: (id: string) => get().entityCache.get(id),

  upsertCache: (update: Persisted<Entity>) => {
    const { entityCache, childIndex, pendingIds, unconfirmed } = get()
    const cacheEntry = entityCache.get(update.id)
    touch(update.id)
    if (!isNewer(update, cacheEntry)) {
      return cacheEntry ?? update
    }
    const patches = unconfirmed.get(update.id)
    const next = rebase(update, patches)
    const writer = createIndexWriter(childIndex)
    writer.reindex(update.id, cacheEntry, next)

    set({
      entityCache: new Map(entityCache).set(update.id, next),
      childIndex: writer.index,
      pendingIds: patches?.length ? pendingIds : withoutId(pendingIds, update.id),
    })
    evictOverCap()

    return next
  },
  upsertCacheEntries: (update: Persisted<Entity>[]) => {
    const { entityCache, childIndex } = get()
    const newMap = new Map(entityCache)
    const writer = createIndexWriter(childIndex)
    let { pendingIds } = get()
    const entries: Persisted<Entity>[] = Array.isArray(update) ? update : [update]
    const newest: Persisted<Entity>[] = []

    for (const entry of entries) {
      touch(entry.id)
      const cacheEntry = newMap.get(entry.id)
      if (cacheEntry && !isNewer(entry, cacheEntry)) {
        newest.push(cacheEntry)
        continue
      }
      const patches = get().unconfirmed.get(entry.id)
      const next = rebase(entry, patches)
      newest.push(next)
      // Update the cache
      writer.reindex(entry.id, cacheEntry, next)
      newMap.set(entry.id, next)
      if (!patches?.length) {
        pendingIds = withoutId(pendingIds, entry.id)
      }
    }
    set({ entityCache: newMap, childIndex: writer.index, pendingIds })
    evictOverCap()

    return newest
  },
  removeEntry: (id: string) => {
    const { entityCache, childIndex, pendingIds, unconfirmed } = get()
    const cacheEntry = entityCache.get(id)
    if (!cacheEntry) {
      logger.warn('Attempted to remove an entry that does not exist in the cache', id)
      return entityCache
    }
    // Copy before deleting: the live Map is the previous state that subscribers compare against. GH #1913
    const updatedCache = new Map(entityCache)
    updatedCache.delete(id)
    const writer = createIndexWriter(childIndex)
    writer.reindex(id, cacheEntry)
    set({
      entityCache: updatedCache,
      childIndex: writer.index,
      pendingIds: withoutId(pendingIds, id),
      unconfirmed: withoutKey(unconfirmed, id),
    })
    forget(id)
    return updatedCache
  },
  partialUpdateCache: (id: string, partialEntity: Partial<Persisted<Entity>>) => {
    const { entityCache, childIndex } = get()
    const cacheEntry = entityCache.get(id)

    if (!cacheEntry) {
      return undefined
    }

    /**
     * GH #1507: a partial without `updatedAt` is a transient/ephemeral field
     * push (e.g. the agent runtime-status broadcast, which is "never
     * persisted"). It carries no ordering, so the staleness gate below doesn't
     * apply — merge the fields into the cached entity. Because `partialEntity`
     * has no `updatedAt`, the spread preserves `cacheEntry.updatedAt`, so the
     * cache never looks "newer than the server" and refetch logic is
     * unaffected. Previously this warned and dropped the update, leaving
     * graph-cache consumers of `runtimeStatus` stale and spamming the console.
     * Only apply the staleness gate when the partial actually carries an
     * `updatedAt` to compare against.
     */
    if (partialEntity.updatedAt && cacheEntry.updatedAt >= partialEntity.updatedAt) {
      return cacheEntry
    }

    const updated = {
      ...cacheEntry,
      ...partialEntity,
    }
    const writer = createIndexWriter(childIndex)
    writer.reindex(id, cacheEntry, updated)

    set({ entityCache: new Map(entityCache).set(id, updated), childIndex: writer.index })
    touch(id)

    return updated
  },
  optimisticUpdateCache: (id: string, partialEntity: Partial<Persisted<Entity>>) => {
    const { entityCache, childIndex, pendingIds, unconfirmed } = get()
    const cacheEntry = entityCache.get(id)

    if (!cacheEntry) {
      return undefined
    }

    /**
     * GH #1913: keep the server-issued `updatedAt`. Stamping the client clock here made a fast local clock
     * outrank genuinely newer server and peer versions, which the LWW gate then dropped.
     */
    const updated = { ...cacheEntry, ...partialEntity, updatedAt: cacheEntry.updatedAt }
    const writer = createIndexWriter(childIndex)
    writer.reindex(id, cacheEntry, updated)
    nextSeq += 1
    const { updatedAt: _ignored, ...patch } = partialEntity

    set({
      entityCache: new Map(entityCache).set(id, updated),
      childIndex: writer.index,
      pendingIds: pendingIds.has(id) ? pendingIds : new Set(pendingIds).add(id),
      unconfirmed: new Map(unconfirmed).set(id, [...(unconfirmed.get(id) ?? []), { seq: nextSeq, patch }]),
    })
    touch(id)

    return updated
  },
  lastOptimisticSeq: (id: string) => get().unconfirmed.get(id)?.at(-1)?.seq,
  confirmOptimistic: (id: string, seqs: readonly number[]) => {
    const { unconfirmed } = get()
    const patches = unconfirmed.get(id)
    if (!patches) {
      return
    }
    const remaining = patches.filter(({ seq }) => !seqs.includes(seq))
    if (remaining.length === patches.length) {
      return
    }
    set({ unconfirmed: remaining.length ? new Map(unconfirmed).set(id, remaining) : withoutKey(unconfirmed, id) })
  },
  retain: (id: string) => {
    retainRef(id)
    if (get().entityCache.has(id)) {
      touch(id)
    }
  },
  release: (id: string) => {
    if (!releaseRef(id)) {
      return
    }
    const { entityCache, childIndex } = get()
    const now = Date.now()
    for (const touchedId of [id, ...(childIndex.get(id) ?? [])]) {
      if (entityCache.has(touchedId)) {
        touch(touchedId, now)
      }
    }
    if (entityCache.size > maxCachedEntries) {
      scheduleEviction(now + evictionGraceMs)
    }
  },
  evictCold: (maxEntries = maxCachedEntries) => evict(maxEntries).count,
}))
